import { i18n, Locale } from "@lingui/core";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";

// List of supported locales
// https://github.com/iamkun/dayjs/tree/dev/src/locale
import "dayjs/locale/da.js";
import "dayjs/locale/en.js";
import "dayjs/locale/fi.js";
import "dayjs/locale/nb.js";
import "dayjs/locale/sv.js";

dayjs.extend(utc);
dayjs.extend(timezone);

// Locale labels for "Now"s
const NOW_LABELS: Record<string, string> = {
	fi: "Nyt",
	da: "Nu",
	nb: "Nå",
	sv: "Nu",
	en: "Now",
};

// Abbreviated unit formatters per spec
const UNIT_ABBR = {
	fi: {
		min: (v: number) => `${v} min`,
		hour: (v: number) => `${v} t`,
		day: (v: number) => `${v} pv`,
	},
	da: {
		min: (v: number) => `${v} min.`,
		hour: (v: number) => `${v} t.`,
		day: (v: number) => (v === 1 ? "1 dag" : `${v} dage`),
	},
	nb: {
		min: (v: number) => `${v} min.`,
		hour: (v: number) => `${v} t.`,
		day: (v: number) => `${v} dg.`,
	},
	sv: {
		min: (v: number) => `${v} min`,
		hour: (v: number) => `${v} tim`,
		day: (v: number) => (v === 1 ? "1 dag" : `${v} dagar`),
	},
	en: {
		min: (v: number) => `${v} min`,
		hour: (v: number) => `${v} h`,
		day: (v: number) => `${v} d`,
	},
} as const;

const localeToTimezone: Record<Locale, string> = {
	fi: "Europe/Helsinki",
	nb: "Europe/Oslo",
	da: "Europe/Copenhagen",
	sv: "Europe/Stockholm",
};

function formatAbsoluteDateByLocale(target: dayjs.Dayjs, now: dayjs.Dayjs) {
	const sameYear = target.year() === now.year();
	switch (i18n.locale) {
		case "fi":
			// Finnish: 8.8 or 8.8.2024
			return target.format(sameYear ? "D.M." : "D.M.YYYY");
		case "da":
		case "nb":
			// Danish/Norwegian: 8. aug. (same year), else 8.8.24
			return sameYear ? target.format("D. MMM") : target.format("D.M.YY");
		case "sv":
			// Swedish: 8 aug. (same year), else 08.08.24
			return sameYear ? target.format("D MMM") : target.format("DD.MM.YY");
		default:
			// English: 8 Aug (same year), else 8 Aug 2024
			return target.format(sameYear ? "D MMM" : "D MMM YYYY");
	}
}

const dayjsTz = dayjs.tz;

export function formatRelativeDate(date: number | Date, { currentTime }: { currentTime?: number } = {}): string {
	if (date === undefined) return "";
	const tz = localeToTimezone[i18n.locale];
	const adDate = dayjsTz(date, tz).locale(i18n.locale);
	const now = dayjsTz(currentTime, tz).locale(i18n.locale);
	const currentLocale = i18n.locale as "fi" | "da" | "nb" | "sv" | "en";

	const diffSeconds = now.diff(adDate, "second");
	if (diffSeconds < 60) return NOW_LABELS[i18n.locale];

	const diffMinutes = now.diff(adDate, "minute");
	if (diffMinutes < 60) return UNIT_ABBR[currentLocale].min(diffMinutes);

	const diffHours = now.diff(adDate, "hour");
	if (diffHours < 24) return UNIT_ABBR[currentLocale].hour(diffHours);

	const diffDays = now.diff(adDate, "day");
	if (diffDays <= 7) return UNIT_ABBR[currentLocale].day(diffDays);

	return formatAbsoluteDateByLocale(adDate, now);
}
